import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import type { IUiDialogHookCustomContent } from '@experiences/interfaces';
import { UiProgressButton } from '@experiences/ui-common';
import { useRouteResolver } from '@experiences/util';
import {
    Button,
    TextField,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import clsx from 'clsx';
import type { FC } from 'react';
import React, { useCallback } from 'react';
import {
    Controller,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { mutate } from 'swr';

import { OrganizationAdminHome } from '../../../common/constants/RouteNames';
import {
    deleteOnPremOrganization,
    onPremOrganizationManagementUri,
} from '../../../services/organization/OnPremOrganizationService';

const useStyles = makeStyles(theme =>
    createStyles({
        deleteOrgConfirmationDialog: {
            width: '465px',
            maxHeight: '408px',
        },
        orgDeleteName: { color: theme.palette.semantic.colorErrorText },
        deleteButton: {
            color: 'white',
            marginLeft: '0.5em',
            backgroundColor: theme.palette.semantic.colorErrorIcon,
            '&:hover': { backgroundColor: `${theme.palette.semantic.colorErrorText} !important` },
        },
        buttonContainer: {
            marginTop: '1em',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
        confirmDeleteSection: {
            marginTop: '1.5em',
            marginBottom: '1.5em',
        },
        textField: {
            marginTop: '0.5em',
            marginBottom: '0.5em',
            width: '100%',
        },
    }),
);

const OnPremOrganizationDeleteDialogBody: FC<IUiDialogHookCustomContent> = ({
    closeDialog, orgName, orgId,
}) => {
    const history = useHistory();
    const getRoute = useRouteResolver();
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();
    const { getErrorMessage } = useGetErrorInfo();
    const setErrorMessage = useCentralErrorSetter();

    const {
        handleSubmit, formState, watch, control,
    } = useForm<{ confirmText: string }>({
        mode: 'onSubmit',
        criteriaMode: 'all',
        defaultValues: { confirmText: '' },
        shouldUnregister: false,
    });

    const { isSubmitting } = formState;

    const onSubmit = useCallback(async () => {
        try {
            await deleteOnPremOrganization(orgId);
            await mutate([ `${onPremOrganizationManagementUri}/getAllOrganizations` ]);
            history.push(getRoute(OrganizationAdminHome, 'host')); // navigate back to host admin home
        } catch (error) {
            setErrorMessage(await getErrorMessage(error));
        }
        closeDialog(true);
    }, [ closeDialog, orgId, history, getRoute, setErrorMessage, getErrorMessage ]);

    return (
        <form
            className={classes.deleteOrgConfirmationDialog}
            data-cy="delete-org-confirmation-dialog"
            onSubmit={handleSubmit(onSubmit)}
        >
            <Typography>
                {translate({
                    id: process.buildConfigs.orgDeleteMessageForCloud
                        ? 'CLIENT_DELETE_ON_PREM_ORGANIZATION_DETAILS'
                        : 'CLIENT_DELETE_ON_PREM_ORGANIZATION_DETAILS_K8S',
                })}
            </Typography>
            <div className={classes.confirmDeleteSection}>
                <Typography>
                    {translate(
                        { id: 'CLIENT_DELETE_ORGANIZATION_CONFIRM_TEXT' },
                        {
                            0: <strong className={clsx(classes.orgDeleteName)}>
                                {orgName}
                            </strong>,
                        },
                    )}
                </Typography>
                <Controller
                    className={classes.textField}
                    control={control}
                    as={TextField}
                    autoComplete="off"
                    variant="outlined"
                    id="standard-name"
                    name="confirmText"
                    data-cy="delete-organization-confirmation-textfield"
                />
            </div>
            <div className={classes.buttonContainer}>
                <Button
                    key="cancelButton"
                    color="primary"
                    data-cy="delete-organization-confirmation-cancel-button"
                    onClick={() => closeDialog(false)}
                >
                    {translate({ id: 'CLIENT_CANCEL' })}
                </Button>
                <UiProgressButton
                    type="submit"
                    color="inherit"
                    classes={{ colorInherit: classes.deleteButton }}
                    loading={isSubmitting}
                    key="primaryButton"
                    variant="contained"
                    data-cy="delete-organization-confirmation-delete-button"
                    disabled={watch('confirmText') !== orgName}
                >
                    {translate({ id: 'CLIENT_DELETE' })}
                </UiProgressButton>
            </div>
        </form>
    );
};

export default OnPremOrganizationDeleteDialogBody;
